import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { useRazorpay } from "react-razorpay";
import { createOrder, getConfig, getSettings, razorpayCreateOrder, razorpayVerify, razorpayCancel, inr, imgSrc } from "@/api";
import { useCart } from "@/context/CartContext";
import { FadeIn } from "@/components/Reveal";

const EMPTY_FORM = { name: "", email: "", phone: "", address: "", city: "", state: "", pincode: "" };

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.3em] uppercase text-clay">{label}</span>
    <input
      data-testid={testid}
      {...props}
      className="mt-2 w-full bg-ivory-card border border-maroon/20 px-4 py-3.5 text-sm text-maroon-deep placeholder:text-clay/50 focus:outline-none focus:border-gold transition-colors"
    />
  </label>
);

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { error: razorpayError, isLoading: razorpayLoading, Razorpay } = useRazorpay();
  const [form, setForm] = useState(EMPTY_FORM);
  const [payment, setPayment] = useState("cod");
  const [placing, setPlacing] = useState(false);
  const [razorpayEnabled, setRazorpayEnabled] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    getConfig().then((c) => setRazorpayEnabled(c.razorpay_enabled)).catch(() => {});
    getSettings().then(setSettings).catch(() => {});
    if (params.get("cancelled")) {
      toast.error("Payment was cancelled", { description: "Your pieces are still in your wardrobe — try again when ready." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (razorpayError) {
      console.error("Razorpay failed to load:", razorpayError);
    }
  }, [razorpayError]);

  const shippingFee =
    !settings || settings.shipping_fee === 0 || (settings.free_shipping_above > 0 && subtotal >= settings.free_shipping_above)
      ? 0
      : settings.shipping_fee;
  const total = subtotal + shippingFee;

  const methods = [];
  if (settings?.cod_enabled !== false) {
    methods.push({ key: "cod", label: "Cash on Delivery", note: "Pay when your pieces arrive" });
  }
  if (razorpayEnabled) {
    methods.push({
      key: "razorpay",
      label: "Razorpay · UPI, Cards & Netbanking",
      note: "Pay instantly with any UPI app, card, wallet or netbanking",
    });
  }

  useEffect(() => {
    if (methods.length && !methods.find((m) => m.key === payment)) {
      setPayment(methods[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, razorpayEnabled]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openRazorpay = async (order) => {
    if (!Razorpay || razorpayLoading) {
      toast.error("Payment gateway is still loading", {
        description: "Please wait a moment and try again.",
      });
      return;
    }

    if (razorpayError) {
      toast.error("Payment gateway could not load", {
        description: "Please refresh the page and try again.",
      });
      return;
    }

    let paymentCompleted = false;
    let rp;
    try {
      rp = await razorpayCreateOrder(order.id, window.location.origin);
    } catch (error) {
      await razorpayCancel(order.id).catch(() => {});
      throw error;
    }
    const rzp = new Razorpay({
      key: rp.key_id,
      amount: rp.amount,
      currency: "INR",
      name: "VAIYRA",
      description: `Order ${order.order_number}`,
      order_id: rp.razorpay_order_id,
      prefill: { name: form.name, email: form.email, contact: form.phone },
      theme: { color: "#5C1A1B" },
      handler: async (res) => {
        try {
          await razorpayVerify({ order_id: order.id, ...res });
          paymentCompleted = true;
          clear();
          navigate(`/order-success/${order.id}`);
        } catch {
          toast.error("Payment verification failed", { description: "Contact us with your order number if money was debited." });
        }
      },
      modal: {
        ondismiss: async () => {
          if (!paymentCompleted) {
            await razorpayCancel(order.id).catch(() => {});
            toast.error("Payment cancelled", { description: "Your pieces have been released back into stock." });
          }
        },
      },
    });
    rzp.open();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your wardrobe is empty", { description: "Add a piece before checking out." });
      return;
    }

    if (!payment) {
      toast.error("Please select a payment method");
      return;
    }

    setPlacing(true);
    try {
      const { order } = await createOrder({
        customer: form,
        items: items.map((i) => ({
          product_id: i.id,
          name: i.name,
          size: i.size,
          qty: i.qty,
          price: i.price,
          image: i.image,
        })),
        payment_method: payment,
        origin_url: window.location.origin,
      });

      if (payment === "razorpay") {
        await openRazorpay(order);
        return;
      }

      clear();
      navigate(`/order-success/${order.id}`);
    } catch {
      toast.error("Could not place the order", { description: "Please try again in a moment." });
    } finally {
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="pt-40 pb-32 text-center px-6">
        <p className="font-display italic text-3xl text-maroon-deep/70">Your wardrobe is still empty.</p>
        <Link
          to="/shop"
          data-testid="checkout-empty-shop-link"
          className="mt-6 inline-block text-xs tracking-[0.3em] uppercase text-gold-dark border-b border-gold pb-1"
        >
          Explore the Collection
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="pt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-12 py-8 sm:py-12 pb-24">
        <Link
          to="/shop"
          data-testid="checkout-back-link"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-clay hover:text-gold-dark transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Continue Browsing
        </Link>

        <FadeIn>
          <h1 className="mt-5 font-display text-3xl sm:text-5xl text-maroon-deep">The Fitting Room</h1>
          <p className="mt-3 text-clay">A few details, and your pieces are on their way.</p>
        </FadeIn>

        <form onSubmit={handleSubmit} className="mt-8 sm:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <FadeIn className="order-2 lg:order-1 lg:col-span-7 space-y-5 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Full Name" testid="checkout-name-input" required value={form.name} onChange={set("name")} placeholder="Aisha Sharma" />
              <Field label="Phone" testid="checkout-phone-input" required type="tel" pattern="[0-9+ -]{8,15}" value={form.phone} onChange={set("phone")} placeholder="98765 43210" />
            </div>
            <Field label="Email" testid="checkout-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
            <Field label="Address" testid="checkout-address-input" required value={form.address} onChange={set("address")} placeholder="Flat, street, landmark" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <Field label="City" testid="checkout-city-input" required value={form.city} onChange={set("city")} placeholder="Jaipur" />
              <Field label="State" testid="checkout-state-input" required value={form.state} onChange={set("state")} placeholder="Rajasthan" />
              <Field label="Pincode" testid="checkout-pincode-input" required pattern="[0-9]{6}" value={form.pincode} onChange={set("pincode")} placeholder="302001" />
            </div>

            <div className="pt-4">
              <p className="text-[10px] tracking-[0.3em] uppercase text-clay mb-4">Payment</p>
              <div className="space-y-3">
                {methods.map((m) => (
                  <label
                    key={m.key}
                    data-testid={`payment-option-${m.key}`}
                    className={`flex items-center gap-4 border px-5 py-4 cursor-pointer transition-all duration-300 ${
                      payment === m.key ? "border-gold bg-ivory-card" : "border-maroon/15 hover:border-gold/50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value={m.key}
                      checked={payment === m.key}
                      onChange={() => setPayment(m.key)}
                      className="accent-[#5C1A1B]"
                    />
                    <span>
                      <span className="block text-sm text-maroon-deep">{m.label}</span>
                      <span className="block text-xs text-clay/80 mt-0.5">{m.note}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.12} className="order-1 lg:order-2 lg:col-span-5">
            <div className="bg-ivory-card border border-gold/30 p-5 sm:p-8 lg:sticky lg:top-28">
              <p className="text-[10px] tracking-[0.4em] uppercase text-gold-dark">Order Summary</p>
              <ul className="mt-6 space-y-5">
                {items.map((i) => (
                  <li key={i.key} data-testid={`summary-item-${i.key}`} className="flex gap-4">
                    <div className="w-14 h-16 overflow-hidden ring-1 ring-gold/30 shrink-0">
                      <img src={imgSrc(i.image)} alt={i.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-base text-maroon-deep leading-tight">{i.name}</p>
                      <p className="text-[10px] tracking-[0.2em] uppercase text-clay mt-1">
                        Size {i.size} · Qty {i.qty}
                      </p>
                    </div>
                    <p className="font-display text-maroon-deep">{inr(i.price * i.qty)}</p>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-gold/25 space-y-2 text-sm">
                <div className="flex justify-between text-clay">
                  <span>Subtotal</span>
                  <span>{inr(subtotal)}</span>
                </div>
                <div className="flex justify-between text-clay">
                  <span>Shipping</span>
                  {shippingFee === 0 ? (
                    <span className="text-gold-dark">Complimentary</span>
                  ) : (
                    <span data-testid="checkout-shipping-fee">{inr(shippingFee)}</span>
                  )}
                </div>
                {settings && settings.shipping_fee > 0 && settings.free_shipping_above > 0 && shippingFee > 0 && (
                  <p className="text-xs text-gold-dark">Free shipping above {inr(settings.free_shipping_above)}</p>
                )}
                <div className="flex justify-between items-baseline pt-3 border-t border-gold/25">
                  <span className="text-xs tracking-[0.3em] uppercase text-maroon-deep">Total</span>
                  <span data-testid="checkout-total" className="font-display text-2xl text-maroon-deep">{inr(total)}</span>
                </div>
                <p className="text-xs text-clay/70 pt-1">GST included in all prices</p>
              </div>
              <div className="mt-5 pt-5 border-t border-gold/20 space-y-2">
                <div className="flex items-center gap-2 text-xs text-maroon-deep">
                  <Lock className="w-3.5 h-3.5 text-gold-dark" />
                  <span>Your order is securely processed.</span>
                </div>
                <p className="text-[11px] leading-relaxed text-clay/75 pl-5.5">
                  You'll receive your order confirmation and delivery updates by email.
                  {payment === "razorpay" ? " Payments are securely processed by Razorpay." : ""}
                </p>
              </div>
              <button
                type="submit"
                data-testid="checkout-form-submit-button"
                disabled={placing || (payment === "razorpay" && razorpayLoading)}
                className="mt-7 w-full min-h-14 flex items-center justify-center gap-3 bg-maroon text-ivory py-4 text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.35em] uppercase hover:bg-maroon-hover transition-colors duration-300 disabled:opacity-60"
              >
                <Lock className="w-4 h-4" /> {placing ? "Placing Order…" : "Place Order"}
              </button>
            </div>
          </FadeIn>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
